import React, { useState, useEffect } from 'react';
import { FiVideo, FiStar, FiTrendingUp } from 'react-icons/fi';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { ownerAnalyticsAPI } from '../../../api/ownerEndpoints';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { showToast } from '../../../components/common/Toaster';
import { useTheme } from '../../../contexts/ThemeContext';
import { safeParseFloat, safeToFixed } from './utils';
import {
  Section,
  SectionTitle,
  Grid,
  StatCard,
  StatLabel,
  StatValue,
  StatDescription,
  Card,
  ChartContainer,
  LoadingContainer,
  EmptyState,
} from './Analytics.styles';

const Content = ({ startDate, endDate }) => {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchData();
  }, [startDate, endDate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = { 
        subjects_order: 'desc', 
        subjects_limit: 10, 
        courses_order: 'desc', 
        courses_limit: 10,
        start_date: startDate ? `${startDate}T00:00:00Z` : undefined,
        end_date: endDate ? `${endDate}T23:59:59Z` : undefined,
      };
      const response = await ownerAnalyticsAPI.getContent(params);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setLoading(false);
    }
  };

  const prepareSubjectData = () => {
    if (!data?.subjects_time) return [];
    return data.subjects_time.map((item) => ({
      name: item.subject,
      hours: safeParseFloat(item.hours),
    }));
  };

  const prepareCoursesData = () => {
    if (!data?.top_courses_by_completion) return [];
    return data.top_courses_by_completion.slice(0, 5).map((item) => ({
      name: item.title.length > 20 ? item.title.substring(0, 20) + '...' : item.title,
      completions: safeParseFloat(item.completed),
    }));
  };

  if (loading) {
    return (
      <LoadingContainer>
        <LoadingSpinner size="large" text="Loading content analytics..." />
      </LoadingContainer>
    );
  }

  if (!data) {
    return (
      <EmptyState>
        <FiVideo size={64} />
        <h3>No Content Data</h3>
        <p>Content analytics will appear when available</p>
      </EmptyState>
    );
  }

  const COLORS = [
    theme.colors.primary,
    theme.colors.secondary,
    theme.colors.accent,
    theme.colors.info,
    '#8B5CF6',
    '#EC4899',
  ];

  const subjectData = prepareSubjectData();
  const coursesData = prepareCoursesData();

  return (
    <>
      <Section>
        <SectionTitle>
          <FiStar />
          Content Ratings
        </SectionTitle>
        <Grid minWidth="250px">
          <StatCard>
            <StatLabel>Avg Content Rating</StatLabel>
            <StatValue>{safeToFixed(data.ratings?.average_content_rating, 1)}</StatValue>
            <StatDescription>Out of 5 stars</StatDescription>
          </StatCard>

          <StatCard>
            <StatLabel>Avg Teacher Rating</StatLabel>
            <StatValue>{safeToFixed(data.ratings?.average_teacher_rating, 1)}</StatValue>
            <StatDescription>Out of 5 stars</StatDescription>
          </StatCard>
        </Grid>
      </Section>

      {subjectData.length > 0 && (
        <Section>
          <SectionTitle>
            <FiTrendingUp />
            Watch Time by Subject
          </SectionTitle>
          <Card>
            <ChartContainer height="300px">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={subjectData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="hours"
                  >
                    {subjectData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `${value.toFixed(1)} hours`} />
                </PieChart>
              </ResponsiveContainer>
            </ChartContainer>
          </Card>
        </Section>
      )}

      {coursesData.length > 0 && (
        <Section>
          <SectionTitle>
            <FiVideo />
            Top Courses by Completions
          </SectionTitle>
          <Card>
            <ChartContainer height="300px">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={coursesData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.border} />
                  <XAxis dataKey="name" stroke={theme.colors.textSecondary} />
                  <YAxis stroke={theme.colors.textSecondary} />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: theme.colors.surface,
                      border: `1px solid ${theme.colors.border}`,
                      borderRadius: theme.borderRadius.md,
                    }}
                  />
                  <Bar dataKey="completions" fill={theme.colors.primary} />
                </BarChart>
              </ResponsiveContainer>
            </ChartContainer>
          </Card>
        </Section>
      )}
    </>
  );
};

export default Content;

